/**
 * Drawer Builder — Mobile Filter Rehaul
 *
 * Replaces the legacy 3 "Search By ..." dropdowns on mobile with a unified
 * chip rail + bottom-sheet refine pattern. Re-uses the existing legacy
 * `.filter-tags-checkbox-N input` checkboxes as the source of truth so
 * `applyFiltersAndPaginate` keeps working unchanged.
 *
 * Mobile-only. Desktop layout still sees the legacy controls (CSS hides
 * the new rail outside the mobile breakpoint).
 *
 * Layers (ordered top-down):
 *  1. FilterStore: in-memory mirror of the 3 filter axes + colour + fits-toggle.
 *     Hydrates from DOM on init; writes through on every change so legacy
 *     handlers keep working. Notifies subscribers.
 *  2. URL persistence: serialise FilterStore -> URLSearchParams via pushState;
 *     popstate restores state.
 *  3. Chip rail: horizontally scrolling pills. Each axis pill opens the refine
 *     sheet at that axis; Fits-toggle pill flips the layout-fit hidden filter.
 *  4. Refine sheet: bottom-sheet modal with per-axis sections, options +
 *     per-option counts (cross-axis), live "Show N results" CTA.
 *  5. Active strip: row of "<axis>: <value> x" chips below the rail when
 *     filters are active. Tapping x removes that single filter.
 */
(function () {
  'use strict';

  function mdT(key, variables) {
    if (window.FoxTheme && FoxTheme.i18n && typeof FoxTheme.i18n.t === 'function') {
      return FoxTheme.i18n.t(key, variables);
    }
    return key;
  }

  // Breakpoints come from MD_CONST (assets/drawer-builder-constants.js) with a
  // literal fallback per the constants contract; this file only needs the
  // desktop side (DESKTOP_BREAKPOINT, read where the shelf/dropdown checks run).
  var DEBUG = window.MD_DEBUG_FILTER === true;
  function dbg() {
    if (DEBUG && window.console && window.console.log) {
      window.console.log.apply(window.console, ['[FilterRehaul]'].concat([].slice.call(arguments)));
    }
  }

  // ---------------- FilterStore ----------------

  // ADDING A FILTER AXIS — update these together (this file is the source of
  // truth for the axis set on both desktop and mobile):
  //   1. AXES + AXIS_CONTAINER + AXIS_LABEL + AXIS_PARAM below.
  //   2. A matching <button data-db-axis="…"> chip in the rail markup
  //      (sections/drawer-image.liquid) and a `.filter-tags-checkbox-N` container.
  //   3. Products need the corresponding token in their `data-tags`.
  // The mobile segmented tiles (drawer-builder-color-chip.js) derive themselves
  // from the rail chips, so they adapt automatically — no edit needed there.
  var AXES = ['type', 'size', 'utensil'];
  // axis -> selector for the legacy checkbox container
  var AXIS_CONTAINER = {
    size: '.filter-tags-checkbox-1',
    utensil: '.filter-tags-checkbox-2',
    type: '.filter-tags-checkbox-3',
  };
  var AXIS_LABEL = {
    type: mdT('modu.builder.filter_type'),
    size: mdT('modu.builder.filter_size'),
    utensil: mdT('modu.builder.filter_utensil'),
  };
  // URL param keys (short, lowercase, comma-separated values)
  var AXIS_PARAM = { type: 'type', size: 'size', utensil: 'utensil' };

  var state = {
    type: new Set(),
    size: new Set(),
    utensil: new Set(),
    color: null, // not yet exposed in chip rail; reserved for future
    fit: true,   // 'Fits my drawer' default ON (matches legacy hidden behaviour)
  };
  var subscribers = [];

  function snapshot() {
    return {
      type: Array.from(state.type),
      size: Array.from(state.size),
      utensil: Array.from(state.utensil),
      color: state.color,
      fit: state.fit,
    };
  }

  function notify(reason) {
    var snap = snapshot();
    subscribers.forEach(function (fn) {
      try { fn(snap, reason); } catch (e) { /* swallow */ }
    });
  }

  // Re-run the legacy `applyFiltersAndPaginate` after store mutations.
  //
  // applyFiltersAndPaginate is a closure inside legacy-inline.js's IIFE
  // and not directly callable from this scope. The legacy code listens
  // for the `db:products-rendered` window event (originally fired once
  // at initial product render) and re-runs apply+prune in response, so
  // we re-use that event as a generic "filters changed; re-evaluate"
  // signal. Coalesced via rAF to absorb bursts (e.g. clearAll which
  // toggles many checkboxes back-to-back).
  var reapplyScheduled = false;
  function scheduleReapply() {
    if (reapplyScheduled) return;
    reapplyScheduled = true;
    requestAnimationFrame(function () {
      reapplyScheduled = false;
      try {
        window.dispatchEvent(new CustomEvent('db:products-rendered'));
      } catch (_) {
        // CustomEvent unsupported (very old browsers): fall back.
        try {
          var ev = document.createEvent('Event');
          ev.initEvent('db:products-rendered', true, true);
          window.dispatchEvent(ev);
        } catch (_) {}
      }
    });
  }

  function hydrateFromDom() {
    AXES.forEach(function (axis) {
      var container = document.querySelector(AXIS_CONTAINER[axis]);
      state[axis].clear();
      if (!container) return;
      var checked = container.querySelectorAll('input[type="checkbox"]:checked');
      checked.forEach(function (cb) {
        var v = (cb.value || '').toString().trim().toLowerCase();
        if (v) state[axis].add(v);
      });
    });
    var activeColor = document.querySelector('.colorsOption span.active');
    state.color = activeColor ? (activeColor.getAttribute('data-id') || null) : null;
  }

  // Toggle a value on an axis (true = add, false = remove, undefined = flip)
  function toggleAxis(axis, value, force) {
    if (AXES.indexOf(axis) === -1) return;
    var v = (value || '').toString().trim().toLowerCase();
    if (!v) return;
    var has = state[axis].has(v);
    var next = (typeof force === 'boolean') ? force : !has;
    if (next === has) return;
    if (next) state[axis].add(v); else state[axis].delete(v);
    // Light selection tick (Forage tap haptic). No-op where unsupported.
    window.mdHaptic && window.mdHaptic(8);

    // Mirror to the legacy checkbox so applyFiltersAndPaginate sees it.
    var container = document.querySelector(AXIS_CONTAINER[axis]);
    if (container) {
      // Match by value, case-insensitive trimmed
      var inputs = container.querySelectorAll('input[type="checkbox"]');
      var matched = false;
      for (var i = 0; i < inputs.length; i += 1) {
        var cb = inputs[i];
        var cbv = (cb.value || '').toString().trim().toLowerCase();
        if (cbv === v) {
          if (cb.checked !== next) {
            cb.checked = next;
            // Fire change so legacy jQuery handlers run badge updates +
            // applyFiltersAndPaginate.
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
          matched = true;
          break;
        }
      }
      if (!matched) dbg('toggleAxis: no checkbox match', axis, v);
    }
    scheduleReapply();
    notify('toggle:' + axis);
  }

  function clearAxis(axis) {
    if (state[axis].size === 0) return;
    state[axis].clear();
    var container = document.querySelector(AXIS_CONTAINER[axis]);
    if (container) {
      var inputs = container.querySelectorAll('input[type="checkbox"]:checked');
      inputs.forEach(function (cb) {
        cb.checked = false;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    scheduleReapply();
    notify('clear:' + axis);
  }

  function clearAll() {
    // Each unchecked box dispatches `change` → legacy mirror → a pushState.
    // Suppress those so one "Clear all" tap is a single history entry
    // instead of one Back-step per cleared option.
    SUPPRESS_URL_WRITE = true;
    AXES.forEach(function (axis) {
      if (state[axis].size > 0) {
        state[axis].clear();
        var container = document.querySelector(AXIS_CONTAINER[axis]);
        if (container) {
          var inputs = container.querySelectorAll('input[type="checkbox"]:checked');
          inputs.forEach(function (cb) {
            cb.checked = false;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      }
    });
    SUPPRESS_URL_WRITE = false;
    // "Clear all" means all: drop the tap-to-fit space filter too (no-op when
    // none is active; MD_clearSpaceFit re-runs the pagination itself).
    try {
      if (window.MD_spaceFit && typeof window.MD_clearSpaceFit === 'function') {
        window.MD_clearSpaceFit();
      }
    } catch (_) {}
    scheduleReapply();
    notify('clearAll');
  }

  function setFit(next) {
    next = !!next;
    if (state.fit === next) return;
    state.fit = next;
    // Bridge to drawer-builder.js's layout-fit filter via its existing
    // "Show all" toggle. Fit ON  -> showAll OFF (only fitting products).
    //                    Fit OFF -> showAll ON  (every product visible,
    // non-fitting ones marked with `layout-filter-disabled`).
    var legacyToggle = document.querySelector(
      '.layout-filter-status__toggle input[name="layout-filter-show-all"]'
    );
    if (legacyToggle) {
      var desired = !next; // showAll = !fit
      if (legacyToggle.checked !== desired) {
        legacyToggle.checked = desired;
        legacyToggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    document.body.classList.toggle('db-fit-off', !next);
    notify('setFit');
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    subscribers.push(fn);
    return function unsubscribe() {
      var idx = subscribers.indexOf(fn);
      if (idx >= 0) subscribers.splice(idx, 1);
    };
  }

  // Expose for other modules + debugging.
  var FilterStore = {
    AXES: AXES.slice(),
    AXIS_LABEL: Object.assign({}, AXIS_LABEL),
    getState: snapshot,
    toggle: toggleAxis,
    clearAxis: clearAxis,
    clearAll: clearAll,
    setFit: setFit,
    subscribe: subscribe,
    hydrate: hydrateFromDom,
  };
  window.MD_FilterStore = FilterStore;

  // ---------------- URL persistence ----------------
  // Serialize FilterStore axes (type/size/utensil) + fit to URL params and
  // restore on load / popstate. We pushState() on every user-driven change,
  // and replaceState() on hydration to avoid polluting history.

  var SUPPRESS_URL_WRITE = false;

  // Set when a swipe-to-dismiss gesture ends. WebKit/Blink still fire a
  // synthesized click after touchend even though touchmove called
  // preventDefault, so the refine-sheet click handler ignores clicks
  // within this window to stop a drag from toggling a checkbox / close /
  // clear / apply as the finger lifts. Mirrors drawer-builder-legacy.js.
  var lastRefineGestureAt = 0;
  var REFINE_GESTURE_CLICK_SUPPRESS_MS = 400;

  function readUrlIntoState() {
    var params = new URLSearchParams(window.location.search);
    SUPPRESS_URL_WRITE = true;
    AXES.forEach(function (axis) {
      var raw = params.get(AXIS_PARAM[axis]);
      var values = (raw || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
      // Clear and re-toggle to sync DOM checkboxes.
      // (toggle handles DOM mirroring + change dispatch.)
      // First clear what's currently set but not in URL:
      var current = Array.from(state[axis]);
      current.forEach(function (v) { if (values.indexOf(v) === -1) toggleAxis(axis, v, false); });
      values.forEach(function (v) { toggleAxis(axis, v, true); });
    });
    // Default is fit ON; only fit=0/off disables it. An absent param must
    // restore ON, otherwise Back-navigating from ?fit=0 to a URL without
    // fit leaves the in-memory state (and UI) stuck OFF.
    var fit = params.get('fit');
    setFit(!(fit === '0' || fit === 'off'));
    SUPPRESS_URL_WRITE = false;
  }

  function writeUrlFromState(replace) {
    if (SUPPRESS_URL_WRITE) return;
    var params = new URLSearchParams(window.location.search);
    AXES.forEach(function (axis) {
      var values = Array.from(state[axis]);
      if (values.length === 0) {
        params.delete(AXIS_PARAM[axis]);
      } else {
        params.set(AXIS_PARAM[axis], values.join(','));
      }
    });
    if (state.fit === false) {
      params.set('fit', '0');
    } else {
      params.delete('fit');
    }
    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    if (newUrl === window.location.pathname + window.location.search + window.location.hash) return;
    try {
      if (replace) {
        window.history.replaceState({}, '', newUrl);
      } else {
        window.history.pushState({}, '', newUrl);
      }
    } catch (_) {
      // Some old browsers / sandboxed iframes block pushState; ignore.
    }
  }

  // ---------------- Counts ----------------
  // Compute count of `.list-view-item.mineProList` matching a hypothetical
  // FilterStore state. Used both for per-option counts (cross-filter) and
  // the live "Show N results" CTA.

  function getProductTagSet(item) {
    if (!item.__dbTagSet) {
      var raw = item.getAttribute('data-tags') || '';
      var set = new Set();
      raw.split(',').forEach(function (t) {
        var s = (t || '').trim().toLowerCase();
        if (s) set.add(s);
      });
      item.__dbTagSet = set;
    }
    return item.__dbTagSet;
  }

  function matchesAxis(item, axis, values) {
    if (!values || values.length === 0) return true;
    var tags = getProductTagSet(item);
    for (var i = 0; i < values.length; i += 1) {
      if (tags.has(values[i])) return true;
    }
    return false;
  }

  function selectedSpaceFit() {
    var fit = window.MD_spaceFit;
    return !isDesktopViewport() && fit && fit.maxW > 0 && fit.maxL > 0 ? fit : null;
  }

  function matchesSpaceFit(item) {
    var fit = selectedSpaceFit();
    if (!fit) return true;
    var width = parseFloat(item.getAttribute('grid-width')) || 0;
    var length = parseFloat(item.getAttribute('grid-length')) || 0;
    return width > 0 && length > 0 && (
      (width <= fit.maxW && length <= fit.maxL) ||
      (length <= fit.maxW && width <= fit.maxL)
    );
  }

  function countMatches(hypothetical) {
    // The selected free region narrows results as an additional Size filter.
    // Colour and whole-drawer fit are still ignored for these option counts.
    var items = document.querySelectorAll('.list-view-item.mineProList');
    var n = 0;
    items.forEach(function (item) {
      if (
        matchesAxis(item, 'type', hypothetical.type) &&
        matchesAxis(item, 'size', hypothetical.size) &&
        matchesAxis(item, 'utensil', hypothetical.utensil) &&
        matchesSpaceFit(item)
      ) n += 1;
    });
    return n;
  }

  function countWithExtraOption(axis, value) {
    var s = snapshot();
    var current = (s[axis] || []).slice();
    if (current.indexOf(value) === -1) current.push(value);
    var hypo = {
      type: axis === 'type' ? current : s.type,
      size: axis === 'size' ? current : s.size,
      utensil: axis === 'utensil' ? current : s.utensil,
    };
    return countMatches(hypo);
  }

  function countCurrent() {
    return countMatches(snapshot());
  }

  // ---------------- Available options (per axis) ----------------
  // Pull options from the legacy DOM (already filtered server-side) so we
  // stay in sync with the source-of-truth values without re-querying
  // metaobjects.

  function getAxisOptions(axis) {
    var container = document.querySelector(AXIS_CONTAINER[axis]);
    if (!container) return [];
    var labels = container.querySelectorAll('label.checkbox-wrapper');
    var seen = new Set();
    var out = [];
    labels.forEach(function (lbl) {
      // Skip empties pruned by legacy logic (data-db-empty="1")
      if (lbl.getAttribute('data-db-empty') === '1') return;
      var input = lbl.querySelector('input[type="checkbox"]');
      if (!input) return;
      var v = (input.value || '').toString().trim().toLowerCase();
      if (!v || seen.has(v)) return;
      seen.add(v);
      var labelEl = lbl.querySelector('.db-filter-option-label');
      var display = labelEl ? labelEl.textContent.trim() : v;
      out.push({ value: v, label: display });
    });
    if (axis === 'size') {
      // Numeric sort (2x1, 2x3, 10x3), not the DOM's lexicographic order
      // (1x2, 10x3, 14x4, 2x1) which scatters related sizes.
      out.sort(function (a, b) {
        var ma = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(a.value);
        var mb = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(b.value);
        if (ma && mb) return (+ma[1] - +mb[1]) || (+ma[2] - +mb[2]);
        if (ma) return -1;
        if (mb) return 1;
        return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
      });
    }
    return out;
  }

  // ---------------- Chip rail ----------------

  function renderChipRail() {
    var rail = document.querySelector('[data-db-rail]');
    if (!rail) return;
    var s = snapshot();
    AXES.forEach(function (axis) {
      var btn = rail.querySelector('[data-db-axis="' + axis + '"]');
      if (!btn) return;
      var n = s[axis].length + (axis === 'size' && selectedSpaceFit() ? 1 : 0);
      btn.classList.toggle('is-active', n > 0);
      // Update label: "Type" or "Type · 2"
      var labelTxt = AXIS_LABEL[axis] + (n > 0 ? ' · ' + n : '');
      var labelSpan = btn.querySelector('.db-chip__label');
      if (labelSpan) labelSpan.textContent = labelTxt; else btn.textContent = labelTxt;
      btn.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
    });
    // Fit toggle pill
    var fitBtn = rail.querySelector('[data-db-fit-toggle]');
    if (fitBtn) {
      fitBtn.classList.toggle('is-active', s.fit === true);
      fitBtn.setAttribute('aria-pressed', s.fit === true ? 'true' : 'false');
    }
    // Clear button visibility includes the selected free region.
    var clearBtn = rail.querySelector('[data-db-clear]');
    if (clearBtn) {
      var anyActive = (s.type.length + s.size.length + s.utensil.length) > 0 ||
        !!(window.MD_spaceFit && window.MD_spaceFit.maxW);
      clearBtn.hidden = !anyActive;
    }
  }

  // On mobile the selected free region is a Size selection, with its own
  // checked option in that sheet. Desktop keeps the fit-bar region chip.
  // legacy-inline dispatches 'md:spacefit' on apply/clear.
  function renderFitChip() {
    var rail = document.querySelector('[data-db-rail]');
    if (!rail) return;
    var chip = rail.querySelector('[data-db-space-chip]');
    var fit = window.MD_spaceFit;
    if (!fit || !fit.maxW || !fit.maxL || !isDesktopViewport()) {
      if (chip) chip.remove();
      renderChipRail();
      return;
    }
    if (!chip) {
      chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'db-chip db-chip--space is-active';
      chip.setAttribute('data-db-space-chip', '');
      chip.addEventListener('click', function () {
        if (typeof window.MD_clearSpaceFit === 'function') window.MD_clearSpaceFit();
      });
      // Live in the fit bar as a segment next to "Fits drawer" (paired-bar
      // layout). Fall back to the rail if the group wrapper isn't present
      // (e.g. an older cached template). insertBefore must target the actual
      // parent of the reference node, so append within the chosen host.
      var fitBar = rail.querySelector('[data-db-fit-bar]');
      var host = fitBar || rail;
      var fitToggle = host.querySelector('[data-db-fit-toggle]');
      if (fitToggle && fitToggle.nextSibling) host.insertBefore(chip, fitToggle.nextSibling);
      else host.appendChild(chip);
    }
    // The desktop fit bar supplies the "Fits" context. Length first matches
    // module titles ("10×3 grid" = 10 long × 3 wide).
    var dim = fit.maxL + '×' + fit.maxW;
    chip.innerHTML = '<span class="db-chip__label">' + dim +
      '</span><span class="db-chip__x" aria-hidden="true">✕</span>';
    chip.setAttribute('aria-label', mdT('modu.builder.clear_space_filter', { dimensions: dim }));
    chip.title = mdT('modu.builder.clear_this_filter');
    renderChipRail();
  }

  function wireChipRail() {
    var rail = document.querySelector('[data-db-rail]');
    if (!rail) return;

    rail.addEventListener('click', function (e) {
      var t = e.target.closest('[data-db-axis], [data-db-fit-toggle], [data-db-clear]');
      if (!t || !rail.contains(t)) return;
      if (t.hasAttribute('data-db-fit-toggle')) {
        setFit(!state.fit);
        return;
      }
      if (t.hasAttribute('data-db-clear')) {
        clearAll();
        return;
      }
      var axis = t.getAttribute('data-db-axis');
      if (axis) { refineAnchor = t; openRefineSheet(axis); }
    });
  }

  // ---------------- Desktop inline filter shelf ----------------
  // On desktop (>= DESKTOP_BREAKPOINT) an axis chip opens a full-width shelf
  // under the chip rail instead of the refine dialog: nothing covers the
  // products being filtered, options lay out as wrapping toggle chips, and
  // every tap applies instantly through the same FilterStore paths. The
  // refine sheet stays the mobile presentation. openRefineSheet() branches
  // here so every entry path (rail click, colorchip proxy) gets the shelf.

  var shelfEl = null;
  var shelfAxis = null;

  function isDesktopViewport() {
    var dtMin = (window.MD_CONST && window.MD_CONST.DESKTOP_BREAKPOINT) || 1000;
    return !!(window.matchMedia && window.matchMedia('(min-width: ' + dtMin + 'px)').matches);
  }

  function ensureShelf() {
    if (shelfEl && document.body.contains(shelfEl)) return shelfEl;
    var rail = document.querySelector('[data-db-rail]');
    if (!rail || !rail.parentNode) return null;
    shelfEl = document.createElement('div');
    shelfEl.className = 'db-filter-shelf';
    shelfEl.setAttribute('data-db-shelf', '');
    shelfEl.innerHTML =
      '<div class="db-filter-shelf__inner">' +
        '<div class="db-filter-shelf__head">' +
          '<span class="db-filter-shelf__title" data-db-shelf-title></span>' +
          '<span class="db-filter-shelf__count" data-db-shelf-count aria-live="polite"></span>' +
        '</div>' +
        '<div class="db-filter-shelf__chips" data-db-shelf-chips role="group"></div>' +
      '</div>';
    rail.parentNode.insertBefore(shelfEl, rail.nextSibling);
    shelfEl.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-db-shelf-value]');
      if (!chip) return;
      toggleAxis(
        chip.getAttribute('data-db-shelf-axis'),
        chip.getAttribute('data-db-shelf-value'),
        !chip.classList.contains('is-on')
      );
    });
    return shelfEl;
  }

  function renderShelf(axis) {
    var el = ensureShelf();
    if (!el) return;
    var s = snapshot();
    var opts = getAxisOptions(axis);
    el.querySelector('[data-db-shelf-title]').textContent = AXIS_LABEL[axis] || axis;
    var html = [];
    opts.forEach(function (opt) {
      var on = s[axis].indexOf(opt.value) !== -1;
      html.push(
        '<button type="button" class="db-shelf-chip' + (on ? ' is-on' : '') + '"' +
          ' data-db-shelf-axis="' + escapeAttr(axis) + '"' +
          ' data-db-shelf-value="' + escapeAttr(opt.value) + '"' +
          ' aria-pressed="' + (on ? 'true' : 'false') + '">' +
          escapeHtml(opt.label) +
        '</button>'
      );
    });
    el.querySelector('[data-db-shelf-chips]').innerHTML = html.join('');
    updateShelfCount();
  }

  function updateShelfCount() {
    if (!shelfEl) return;
    var c = shelfEl.querySelector('[data-db-shelf-count]');
    if (!c) return;
    var n = countCurrent();
    c.textContent = mdT('modu.builder.showing_modules', { count: n });
  }

  // External state changes (clear-all, popstate, URL restore) flip the chip
  // on-states in place — no innerHTML rebuild, so the shelf never scroll-jumps.
  function syncShelfStates() {
    if (!shelfEl || !shelfAxis) return;
    var s = snapshot();
    shelfEl.querySelectorAll('[data-db-shelf-value]').forEach(function (chip) {
      var on = s[shelfAxis].indexOf(chip.getAttribute('data-db-shelf-value')) !== -1;
      chip.classList.toggle('is-on', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    updateShelfCount();
  }

  function syncShelfRailExpanded() {
    var rail = document.querySelector('[data-db-rail]');
    if (!rail) return;
    rail.querySelectorAll('[data-db-axis]').forEach(function (b) {
      var open = b.getAttribute('data-db-axis') === shelfAxis;
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
      // Desktop segmented-bar: the open axis reads as the pressed white card.
      b.classList.toggle('is-open', open);
    });
  }

  function openShelf(axis) {
    shelfAxis = axis;
    renderShelf(axis);
    if (shelfEl) shelfEl.classList.add('is-open');
    syncShelfRailExpanded();
  }

  function closeShelf() {
    if (!shelfAxis) return;
    shelfAxis = null;
    if (shelfEl) shelfEl.classList.remove('is-open');
    syncShelfRailExpanded();
  }

  function toggleShelf(axis) {
    if (shelfAxis === axis) { closeShelf(); return; }
    openShelf(axis);
  }

  // Dismiss any open filter UI (desktop shelf or mobile refine dialog). Called
  // by the builder when a module is added or selected on the canvas, so the
  // filter picker gets out of the way of the piece the user just placed.
  window.MD_closeFilterUI = function () {
    try { closeShelf(); } catch (_) {}
    try { closeRefineSheet(); } catch (_) {}
    if (typeof window.MD_closeColourSheet === 'function') window.MD_closeColourSheet();
  };

  // Desktop only: position the refine panel as a dropdown anchored under the
  // chip that opened it (clamped to the viewport). On mobile (or with no anchor)
  // it clears the inline styles so the CSS bottom-sheet/fallback applies.
  function positionRefineDropdown() {
    if (!refineRoot) return;
    var panel = refineRoot.querySelector('.db-refine-sheet__panel');
    if (!panel) return;
    // Desktop = at/above the desktop breakpoint (1000). Below that (incl. tablets)
    // the refine UI is a bottom sheet, matching the builder's single boundary.
    var dtMin = (window.MD_CONST && window.MD_CONST.DESKTOP_BREAKPOINT) || 1000;
    var desktop = window.matchMedia && window.matchMedia('(min-width: ' + dtMin + 'px)').matches;
    if (!desktop || !refineAnchor) {
      panel.style.position = '';
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.maxHeight = '';
      return;
    }
    var PW = 320, GAP = 8, MARGIN = 12;
    var r = refineAnchor.getBoundingClientRect();
    var left = Math.min(r.right - PW, window.innerWidth - PW - MARGIN);
    left = Math.max(MARGIN, left);
    var top = r.bottom + GAP;
    panel.style.position = 'fixed';
    panel.style.top = top + 'px';
    panel.style.left = left + 'px';
    panel.style.right = 'auto';
    panel.style.maxHeight = Math.max(180, window.innerHeight - top - MARGIN) + 'px';
  }

  // ---------------- Refine sheet ----------------

  var refineRoot = null;
  var refineLastFocused = null;
  var refineEscHandler = null;
  var refineIsOpen = false;
  var refineHideTimer = null;
  // When set, renderRefineBody() emits only this axis. Cleared on close so
  // a future axis-less open (e.g. programmatic) renders all axes.
  var refineActiveAxis = null;
  // Set true while a sheet `change` handler is mid-flight, so the FilterStore
  // subscriber skips its blanket renderRefineBody() and lets the partial
  // updateRefineOption() path run without scroll-jumping the sheet.
  var refineSheetDrivenChange = false;
  // The chip that opened the sheet — used on desktop to anchor the refine panel
  // as a dropdown directly under it (mobile ignores this and stays a bottom sheet).
  var refineAnchor = null;

  function openRefineSheet(axis) {
    // Desktop gets the inline shelf, not the dialog — branch here so every
    // caller (rail click, colorchip proxy) routes the same way.
    if (isDesktopViewport() && axis) { toggleShelf(axis); return; }
    refineRoot = document.querySelector('[data-db-refine-sheet]');
    if (!refineRoot) return;
    if (refineHideTimer) { clearTimeout(refineHideTimer); refineHideTimer = null; }
    if (!refineIsOpen) refineLastFocused = document.activeElement;
    refineIsOpen = true;
    refineActiveAxis = axis || null;
    renderRefineBody();
    updateRefineCount();
    refineRoot.hidden = false;
    positionRefineDropdown(); // desktop: anchor under the clicked chip
    // Two-frame delay so display:block applies before transition class.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (refineIsOpen) refineRoot.classList.add('is-open');
      });
    });
    // Lock body scroll while open
    document.body.classList.add('db-refine-open');
    // Focus first interactive element (close button)
    var closeBtn = refineRoot.querySelector('[data-db-refine-close]');
    if (closeBtn) try { closeBtn.focus(); } catch (_) {}
    if (!refineEscHandler) {
      refineEscHandler = function (e) { if (e.key === 'Escape') closeRefineSheet(); };
      document.addEventListener('keydown', refineEscHandler);
    }
  }

  function closeRefineSheet() {
    if (!refineRoot || !refineIsOpen) return;
    refineIsOpen = false;
    refineRoot.classList.remove('is-open');
    document.body.classList.remove('db-refine-open');
    refineActiveAxis = null;
    if (refineEscHandler) {
      document.removeEventListener('keydown', refineEscHandler);
      refineEscHandler = null;
    }
    // Wait for transition to finish, then hide.
    var TRANS_MS = 280;
    refineHideTimer = setTimeout(function () {
      refineHideTimer = null;
      if (refineRoot && !refineIsOpen) {
        refineRoot.hidden = true;
        // Clear desktop dropdown positioning so a later (resized) mobile open
        // falls back cleanly to the bottom-sheet styles.
        var panel = refineRoot.querySelector('.db-refine-sheet__panel');
        if (panel) {
          panel.style.position = '';
          panel.style.top = '';
          panel.style.left = '';
          panel.style.right = '';
          panel.style.maxHeight = '';
        }
      }
    }, TRANS_MS);
    refineAnchor = null;
    // Restore focus
    if (refineLastFocused && typeof refineLastFocused.focus === 'function') {
      try { refineLastFocused.focus(); } catch (_) {}
    }
    refineLastFocused = null;
  }

  function renderRefineBody() {
    if (!refineRoot) return;
    var body = refineRoot.querySelector('[data-db-refine-body]');
    if (!body) return;
    var s = snapshot();
    var html = [];
    var axesToRender = refineActiveAxis ? [refineActiveAxis] : AXES;
    axesToRender.forEach(function (axis) {
      var opts = getAxisOptions(axis);
      var spaceFit = axis === 'size' ? selectedSpaceFit() : null;
      if (opts.length === 0 && !spaceFit) return;
      var selectedCount = s[axis].length + (spaceFit ? 1 : 0);
      html.push('<section class="db-refine-axis" data-db-refine-axis="' + axis + '">');
      html.push(
        '<header class="db-refine-axis__header">' +
          '<h3 class="db-refine-axis__title">' + escapeHtml(AXIS_LABEL[axis]) + '</h3>' +
          (selectedCount > 0 ? '<span class="db-refine-axis__count">' + escapeHtml(mdT('modu.builder.selected_count', { count: selectedCount })) + '</span>' : '') +
        '</header>'
      );
      html.push('<ul class="db-refine-axis__options" role="group" aria-label="' + escapeAttr(mdT('modu.builder.filter_options', { axis: AXIS_LABEL[axis] })) + '">');
      if (spaceFit) {
        var dimension = spaceFit.maxL + '×' + spaceFit.maxW;
        html.push(
          '<li class="db-refine-option db-refine-option--space is-checked">' +
            '<label class="db-refine-option__label">' +
              '<input type="checkbox" data-db-refine-space-fit checked>' +
              '<span class="db-refine-option__check" aria-hidden="true"></span>' +
              '<span class="db-refine-option__name">' + escapeHtml(mdT('modu.builder.fits_dimension', { dimensions: dimension })) + '</span>' +
            '</label>' +
          '</li>'
        );
      }
      opts.forEach(function (opt) {
        var checked = s[axis].indexOf(opt.value) !== -1;
        // Per-option count: if NOT yet selected, count is matches if added.
        // If already selected, count is matches with current state (so the
        // user sees how many products the chip currently filters down to).
        var cnt;
        if (checked) {
          cnt = countMatches(s);
        } else {
          cnt = countWithExtraOption(axis, opt.value);
        }
        html.push(
          '<li class="db-refine-option' + (checked ? ' is-checked' : '') + '">' +
            '<label class="db-refine-option__label">' +
              '<input type="checkbox" data-db-refine-input data-db-axis="' + axis + '" value="' + escapeAttr(opt.value) + '"' + (checked ? ' checked' : '') + '>' +
              '<span class="db-refine-option__check" aria-hidden="true"></span>' +
              '<span class="db-refine-option__name">' + escapeHtml(opt.label) + '</span>' +
              '<span class="db-refine-option__count">' + cnt + '</span>' +
            '</label>' +
          '</li>'
        );
      });
      html.push('</ul></section>');
    });
    body.innerHTML = html.join('');
  }

  function updateRefineCount() {
    if (!refineRoot) return;
    var ctaCount = refineRoot.querySelector('[data-db-refine-count]');
    if (ctaCount) ctaCount.textContent = String(countCurrent());
  }

  // Partial DOM update for a single tap. Avoids the scroll-jump and flicker
  // of re-rendering the entire sheet body. Updates the toggled option's
  // checkbox/li state and rewrites every option's count under the visible
  // axis (counts are interdependent: toggling one option shifts the others).
  function updateRefineOption(axis) {
    if (!refineRoot || !axis) return;
    var section = refineRoot.querySelector('[data-db-refine-axis="' + axis + '"]');
    if (!section) return;
    var s = snapshot();
    var items = section.querySelectorAll('.db-refine-option');
    items.forEach(function (li) {
      var input = li.querySelector('[data-db-refine-input]');
      if (!input) return;
      var value = input.value;
      var isChecked = s[axis].indexOf(value) !== -1;
      input.checked = isChecked;
      li.classList.toggle('is-checked', isChecked);
      var countEl = li.querySelector('.db-refine-option__count');
      if (countEl) {
        var cnt = isChecked ? countMatches(s) : countWithExtraOption(axis, value);
        countEl.textContent = String(cnt);
      }
    });
    var headerCount = section.querySelector('.db-refine-axis__count');
    var selectedCount = s[axis].length + (axis === 'size' && selectedSpaceFit() ? 1 : 0);
    if (selectedCount > 0) {
      if (headerCount) {
        headerCount.textContent = mdT('modu.builder.selected_count', { count: selectedCount });
      } else {
        var headerEl = section.querySelector('.db-refine-axis__header');
        if (headerEl) {
          var span = document.createElement('span');
          span.className = 'db-refine-axis__count';
          span.textContent = mdT('modu.builder.selected_count', { count: selectedCount });
          headerEl.appendChild(span);
        }
      }
    } else if (headerCount) {
      headerCount.parentNode.removeChild(headerCount);
    }
  }

  function wireRefineSheet() {
    var root = document.querySelector('[data-db-refine-sheet]');
    if (!root) return;

    // Capture also catches taps on controls above the overlay, even when
    // their own handlers stop the click from bubbling to the sheet.
    document.addEventListener('click', function (e) {
      if (!refineIsOpen || root.querySelector('.db-refine-sheet__panel').contains(e.target)) return;
      if (e.target.closest('[data-db-axis], .db-seg__item')) return;
      closeRefineSheet();
    }, true);

    // Close on backdrop click, close button, apply, clear all
    root.addEventListener('click', function (e) {
      // Swallow the ghost click synthesized right after a swipe-to-dismiss.
      if (Date.now() - lastRefineGestureAt < REFINE_GESTURE_CLICK_SUPPRESS_MS) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var closeT = e.target.closest('[data-db-refine-close]');
      if (closeT) { closeRefineSheet(); return; }
      var clearT = e.target.closest('[data-db-refine-clear]');
      if (clearT) { clearAll(); return; }
      var applyT = e.target.closest('[data-db-refine-apply]');
      if (applyT) { closeRefineSheet(); return; }
      // Tap outside the panel = close. The backdrop already carries
      // `data-db-refine-close` so the explicit handler above usually
      // catches it, but this fallback covers any case where a tap on
      // the dimmed area above the panel doesn't hit the backdrop
      // element directly (e.g. another element overlapping it). Bottom
      // sheets are expected to dismiss on outside-tap; without this,
      // users get stuck and can only exit via "Show N Results".
      if (!e.target.closest('.db-refine-sheet__panel')) {
        closeRefineSheet();
      }
    });

    // Live toggling within the sheet
    root.addEventListener('change', function (e) {
      var spaceInput = e.target.closest('[data-db-refine-space-fit]');
      if (spaceInput) {
        if (!spaceInput.checked && typeof window.MD_clearSpaceFit === 'function') {
          window.MD_clearSpaceFit();
        }
        return;
      }
      var input = e.target.closest('[data-db-refine-input]');
      if (!input) return;
      var axis = input.getAttribute('data-db-axis');
      var value = input.value;
      // Flag the upcoming notify() so the subscriber skips its blanket
      // renderRefineBody() — we'll do a partial update instead, preserving
      // scroll position and avoiding flicker.
      refineSheetDrivenChange = true;
      toggleAxis(axis, value, !!input.checked);
      updateRefineOption(axis);
      updateRefineCount();
      // Clear on the next tick so any other in-flight subscribers (URL
      // mirror, popstate, etc.) revert to the full-render path.
      setTimeout(function () { refineSheetDrivenChange = false; }, 0);
    });
  }

  // Drag-down-to-dismiss. Mirrors the products sheet gesture in
  // drawer-builder-legacy.js but stripped to one job (close, no peek
  // state). Direction-locks at 8px so horizontal taps/scrolls aren't
  // hijacked. Defers to native scroll when the body has scrolled
  // (scrollTop > 0): only when at the top of the option list does a
  // downward drag pull the panel itself. Past 80px or 0.5 px/ms,
  // release closes the sheet; under threshold, snaps back.
  function wireRefineSwipeToDismiss() {
    var sheet = document.querySelector('[data-db-refine-sheet]');
    if (!sheet) return;
    var panel = sheet.querySelector('.db-refine-sheet__panel');
    var body = sheet.querySelector('[data-db-refine-body]');
    if (!panel || !body) return;

    var startX = 0, startY = 0;
    var lastY = 0, lastT = 0;
    var velocity = 0;
    var active = false;
    // null until direction-locked, then 'panel-drag' (we own it),
    // 'native-scroll' (let the body scroll), or 'horizontal' (let it pass)
    var mode = null;
    var DIRECTION_LOCK_PX = 8;
    var TRIGGER_PX = 80;
    var TRIGGER_VELOCITY = 0.5; // px/ms

    function clearInline() {
      panel.style.transition = '';
      panel.style.transform = '';
    }

    panel.addEventListener('touchstart', function (e) {
      if (!sheet.classList.contains('is-open')) return;
      if (e.touches.length !== 1) return;
      var t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastY = startY;
      lastT = Date.now();
      velocity = 0;
      active = true;
      mode = null;
    }, { passive: true });

    panel.addEventListener('touchmove', function (e) {
      if (!active || e.touches.length !== 1) return;
      var t = e.touches[0];
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;

      if (mode === null) {
        if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          mode = 'horizontal';
          active = false;
          return;
        }
        // Vertical lock. Drag the panel only when the option list is
        // at the top — otherwise the user is scrolling content.
        if (dy > 0 && body.scrollTop <= 0) {
          mode = 'panel-drag';
          panel.style.transition = 'none';
        } else {
          mode = 'native-scroll';
          active = false;
          return;
        }
      }

      if (mode === 'panel-drag') {
        // Clamp upward overshoot — we only translate downward.
        var ty = dy > 0 ? dy : 0;
        e.preventDefault();
        panel.style.transform = 'translate3d(0,' + ty + 'px,0)';
        var now = Date.now();
        var dt = now - lastT;
        if (dt > 0) velocity = (t.clientY - lastY) / dt;
        lastY = t.clientY;
        lastT = now;
      }
    }, { passive: false });

    function endGesture() {
      if (mode !== 'panel-drag') {
        active = false;
        mode = null;
        return;
      }
      lastRefineGestureAt = Date.now();
      var dy = lastY - startY;
      active = false;
      mode = null;
      if (dy > TRIGGER_PX || velocity > TRIGGER_VELOCITY) {
        // Restore CSS transition first so the close-slide animates
        // from the dragged position to translateY(100%). closeRefineSheet
        // strips .is-open; clearing the inline transform last hands
        // control back to the CSS rule, which then animates.
        panel.style.transition = '';
        closeRefineSheet();
        panel.style.transform = '';
      } else {
        clearInline();
      }
    }
    panel.addEventListener('touchend', endGesture, { passive: true });
    panel.addEventListener('touchcancel', endGesture, { passive: true });
  }

  // ---------------- Listening for legacy-driven changes ----------------
  // The legacy jQuery handlers fire `change` events on `.filter-tags-checkbox-N
  // input` whenever the user interacts with the (now-hidden) old controls or
  // when applyFiltersAndPaginate runs. We re-hydrate FilterStore so the new
  // UI stays in sync regardless of who flipped the checkbox.

  function wireLegacyMirror() {
    AXES.forEach(function (axis) {
      var sel = AXIS_CONTAINER[axis] + ' input[type="checkbox"]';
      document.addEventListener('change', function (e) {
        if (!e.target.matches || !e.target.matches(sel)) return;
        // Re-hydrate just this axis (cheap)
        var container = document.querySelector(AXIS_CONTAINER[axis]);
        if (!container) return;
        state[axis].clear();
        container.querySelectorAll('input[type="checkbox"]:checked').forEach(function (cb) {
          var v = (cb.value || '').toString().trim().toLowerCase();
          if (v) state[axis].add(v);
        });
        notify('legacyMirror:' + axis);
      });
    });
    // Color: track color row clicks
    document.addEventListener('click', function (e) {
      var span = e.target.closest('.colorsOption span');
      if (!span) return;
      // Defer one tick so .active class is set by the legacy handler first.
      setTimeout(function () {
        var active = document.querySelector('.colorsOption span.active');
        var newColor = active ? (active.getAttribute('data-id') || null) : null;
        if (newColor !== state.color) {
          state.color = newColor;
          notify('color');
        }
      }, 0);
    });
  }

  // ---------------- Init ----------------

  function init() {
    if (!document.body.classList.contains('drawer-builder')) return;

    // Relocate the refine sheet to <body> so its z-index can stack above the
    // checkout bar. The liquid template renders it inside `.builtbundle`,
    // which has `isolation: isolate` (drawer-builder.css ~6177) and so
    // creates a stacking context that traps the sheet's z-index. The
    // checkout bar (`.db-topbar__checkout`, z-index 6002) lives outside
    // `.builtbundle` and would otherwise paint above the refine sheet's
    // panel — visually leaking through the bottom of the sheet.
    var refineSheetEl = document.querySelector('[data-db-refine-sheet]');
    if (refineSheetEl && refineSheetEl.parentNode !== document.body) {
      document.body.appendChild(refineSheetEl);
    }

    // Hydrate first (read DOM checkboxes in case page loaded with state)
    hydrateFromDom();

    // Wire UI
    wireChipRail();
    wireRefineSheet();
    wireRefineSwipeToDismiss();
    wireLegacyMirror();

    // URL persistence: read URL once on load, then write on each change.
    readUrlIntoState();
    // Render after URL applied.
    renderChipRail();
    // If the sheet is open while URL changes, refresh it.
    subscribe(function (s, reason) {
      writeUrlFromState(false);
      renderChipRail();
      syncShelfStates();
      if (refineRoot && refineRoot.classList.contains('is-open')) {
        // Sheet-driven changes do their own partial DOM update (see
        // updateRefineOption) to avoid scroll-jumping the option list.
        // External callers (popstate, legacy mirror, clear-all) still
        // trigger a full re-render below.
        if (!refineSheetDrivenChange) {
          renderRefineBody();
        }
        updateRefineCount();
      }
    });

    // Keep the Size selection, result count, and an open Size sheet in sync
    // when a grid gesture applies or clears the selected free region.
    document.addEventListener('md:spacefit', function () {
      renderFitChip();
      if (refineRoot && refineRoot.classList.contains('is-open')) {
        renderRefineBody();
        updateRefineCount();
      }
    });
    renderFitChip();

    // Desktop shelf lifecycle: Esc closes it; crossing to the mobile
    // breakpoint closes it too (the refine sheet takes over there).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeShelf();
    });
    if (window.matchMedia) {
      var dtMin = (window.MD_CONST && window.MD_CONST.DESKTOP_BREAKPOINT) || 1000;
      var mmDesk = window.matchMedia('(min-width: ' + dtMin + 'px)');
      // Leaving desktop closes the shelf; either crossing re-renders the fit
      // chip so its label (dimension-only on desktop, "Fits L×W" otherwise)
      // matches the layout it lands in.
      var onDeskChange = function (ev) { if (!ev.matches) closeShelf(); renderFitChip(); };
      if (mmDesk.addEventListener) mmDesk.addEventListener('change', onDeskChange);
      else if (mmDesk.addListener) mmDesk.addListener(onDeskChange);
    }

    // popstate: re-read URL into state without writing back.
    window.addEventListener('popstate', function () {
      SUPPRESS_URL_WRITE = true;
      readUrlIntoState();
      SUPPRESS_URL_WRITE = false;
      renderChipRail();
    });

    // Re-render when products finish loading (counts depend on rendered items)
    window.addEventListener('db:products-rendered', function () {
      // Clear any cached tag sets; products may have changed
      document.querySelectorAll('.list-view-item.mineProList').forEach(function (el) {
        delete el.__dbTagSet;
      });
      renderChipRail();
      if (refineRoot && refineRoot.classList.contains('is-open')) {
        renderRefineBody();
        updateRefineCount();
      }
    });

    // Re-render on resize across the breakpoint (CSS handles visibility, but
    // this keeps state in lockstep if the user rotates their device).
    window.addEventListener('resize', function () {
      // Keep the desktop dropdown anchored under its chip on resize.
      if (refineRoot && refineRoot.classList.contains('is-open')) {
        positionRefineDropdown();
      }
    });

    // Surface body class for CSS to gate visibility (also used by the rail
    // wrapper if it needs to know the JS is alive).
    document.body.classList.add('db-filter-rehaul-ready');

    dbg('init complete', snapshot());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    // Defer one tick so the legacy inline handlers attach first.
    setTimeout(init, 0);
  }

  // ---------------- Helpers ----------------

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
})();
